import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { DiscountGovernanceSection, EngagementRulesSection, MentionGrantsSection } from '../'
import SmsLimitsSection from '../SmsLimitsSection'
import FixedFloorSection from '../FixedFloorSection'
import RestrictedTab from '../RestrictedTab'
import { LIMITS_SECTIONS, glanceRows, liveGrantCount, sectionFrom, type GrantsSnapshot } from '../limitsGlance'
import { useWorkspaceStore } from '../../../stores/workspaceStore'
import { salesApi } from '../../../services/api'
import { Card, Eyebrow, Hint, KeyValue, SectionLayout, SectionNav, SectionPanel, Stack, useVisitedSections } from '../../../ds'

/**
 * Limits Tab — the compliance floor plus the commercial limits (App IA v5 · phase 2c,
 * `settings-consolidation.md` §3.6), laid out to v6 (MAR-385): a section nav, ONE section at a
 * time, and an "At a glance" aside. Whether a channel is connected at all lives on Channels; this
 * tab is only ever about how far a connected one may go.
 *
 * ## `?section=` — beside `?tab=`, never instead of it
 *
 * Like the tab, the open section is derived from the URL at the read, with no state mirroring it,
 * so a link to `/authority?tab=limits&section=sms` lands there and Back walks sections. An unknown
 * or missing value opens Discounts. `?tab=sales` (the retired Sales tab, aliased here by
 * `screens/Authority.tsx`) opens Shopify codes, which is what that link was always for. Switching
 * TABS drops `section` (Authority's `setActive`), so it never leaks into another tab's URL.
 *
 * ## A section switch never drops a draft
 *
 * Each section sits in a ds `SectionPanel`: mounted the first time it is opened, then kept
 * mounted but `hidden`/`inert` while another is open, so an unsaved ceiling or cap survives a
 * trip to another section and back, and each section's own read runs once. A workspace switch
 * drops every hidden section in the same render (`useVisitedSections`' reset key).
 *
 * ## Permissions — unchanged by the layout
 *
 * The mention grant ("Shopify codes she may bring up", MAR-296) and the ceiling are both
 * owner/admin margin decisions, enforced on the server — but only the ceiling is behind
 * `RestrictedTab`. The grant card stays visible to a member and renders itself READ-ONLY, so a
 * member meets exactly one "Access restricted" card on Limits: the ceiling's, on Discounts.
 */

/**
 * The grant read the aside counts from, stamped with its workspace and scoped at the read.
 * Until the Shopify codes section has been opened for this store it is not mounted, so the aside
 * makes the same two reads that card makes (`GET /sales/discounts`, `GET /sales`) once per store;
 * once the card IS mounted (open, or kept hidden after a visit), every read it settles replaces
 * this one, so a grant or revoke there is counted at once. A failure is "—", never zero, and
 * never blocks the tab.
 *
 * `paused` while the codes card is mounted: it is making those two reads and reports them through
 * `onRead`, so a second pair from here would only be thrown away. Nor does it read again for a
 * store it already holds a snapshot of (the card's, once it has reported).
 */
const useGrantsGlance = (workspaceId: string | undefined, paused: boolean) => {
  const [snap, setSnap] = useState<GrantsSnapshot | null>(null)
  // Which store the snapshot is for, for the read effect below. Written in an effect (declared
  // first, so it runs first in a commit), never during render.
  const held = useRef<string | null>(null)
  useEffect(() => {
    held.current = snap?.workspaceId ?? null
  }, [snap])

  useEffect(() => {
    if (!workspaceId || paused || held.current === workspaceId) return
    let live = true
    const failed: GrantsSnapshot = {
      workspaceId,
      discounts: [],
      sales: [],
      discountsRead: 'failed',
      salesRead: 'failed',
      discountsTruncated: false,
      salesTruncated: false,
    }
    Promise.all([salesApi.listDiscounts(workspaceId), salesApi.list(workspaceId)])
      .then(([d, s]) => {
        if (!live) return
        setSnap((prev) =>
          // A settled read from the card itself is at least as new — never overwrite it.
          prev && prev.workspaceId === workspaceId
            ? prev
            : {
                workspaceId,
                discounts: d?.success && d.data ? d.data.discounts || [] : [],
                sales: s?.success && s.data ? s.data.sales || [] : [],
                discountsRead: d?.success && d.data ? 'ok' : 'failed',
                salesRead: s?.success && s.data ? 'ok' : 'failed',
                discountsTruncated: d?.data?.truncated === true,
                salesTruncated: s?.data?.truncated === true,
              }
        )
      })
      .catch(() => {
        if (live) setSnap((prev) => (prev && prev.workspaceId === workspaceId ? prev : failed))
      })
    return () => {
      live = false
    }
  }, [workspaceId, paused])

  const report = useCallback((next: GrantsSnapshot) => setSnap(next), [])
  const current = snap && snap.workspaceId === workspaceId ? snap : null
  return { current, report }
}

const LimitsTab = () => {
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const workspaceId = currentWorkspace?.id
  const [searchParams, setSearchParams] = useSearchParams()
  const section = sectionFrom(searchParams)
  const visited = useVisitedSections(section, workspaceId)
  const grants = useGrantsGlance(workspaceId, visited.has('codes'))

  const open = (id: string) => {
    // Merge, never replace — the same rule as `?tab=` (a receipt link's context survives).
    const next = new URLSearchParams(searchParams)
    next.set('section', id)
    setSearchParams(next)
  }

  const rows = glanceRows(currentWorkspace, liveGrantCount(grants.current, new Date().toISOString()))

  const aside = (
    <Card>
      <Stack gap="sm">
        <Eyebrow tone="muted">At a glance</Eyebrow>
        <KeyValue items={rows.map((r) => ({ label: r.label, value: r.value }))} />
        <Hint>
          What happens without asking, within these, is set in <Link to="/authority?tab=authority">Authority</Link>.
        </Hint>
      </Stack>
    </Card>
  )

  return (
    <SectionLayout
      nav={<SectionNav items={LIMITS_SECTIONS} value={section} onChange={open} label="Limits sections" />}
      aside={aside}
      asideLabel="At a glance"
    >
      <SectionPanel id="discounts" value={section} visited={visited}>
        <RestrictedTab>
          <DiscountGovernanceSection onClose={() => {}} />
        </RestrictedTab>
      </SectionPanel>
      {/*
        ⚠️ The `key` is load-bearing, not tidiness. Everything in this card — the discounts,
        the grants standing against them, the live switches that revoke those grants —
        belongs to exactly one store. Keying on the workspace id makes a switch a REMOUNT:
        React tears the old tree down and builds a fresh one in the same render that selects
        the new store, so the previous merchant's rows and controls cannot survive into a
        single paint under the new merchant's name. The section stamps its own state with the
        workspace id as well; this is the structural half of the same guarantee, and it also
        resets the per-row wording editors.
      */}
      <SectionPanel id="codes" value={section} visited={visited}>
        <MentionGrantsSection key={workspaceId ?? 'no-workspace'} onRead={grants.report} />
      </SectionPanel>
      <SectionPanel id="contact" value={section} visited={visited}>
        <EngagementRulesSection onClose={() => {}} />
      </SectionPanel>
      <SectionPanel id="sms" value={section} visited={visited}>
        <SmsLimitsSection />
      </SectionPanel>
      <SectionPanel id="floor" value={section} visited={visited}>
        <FixedFloorSection />
      </SectionPanel>
    </SectionLayout>
  )
}

export default LimitsTab
